import { useLoaderData } from '@remix-run/react'
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'
import type { Key } from 'react-aria'
import CvvInput from './Cvv'
import type { CardTypes } from './Icon'
import CreditCard from './Icon'
import InstallmentsSelect from './InstallmentsSelect'
import { CreditCardContext } from './Provider'
import { Card, Radio, RadioGroup } from '~/design-system/primitives'
import type { loader } from '~/routes/checkout.payment'
import type { AvailableAccount } from '~/type/Cart'
import { EFlagCreditCard } from '~/types/cart.interface'

const CreditCardList = forwardRef((_, ref) => {
  const {
    paymentData: { availableAccounts, installmentOptions, paymentSystems },
  } = useLoaderData<typeof loader>()
  const {
    setAccountId,
    setValidationCode,
    validationCode,
    setCreditCard,
    installmentsSelected,
    setInstallmentsSelected,
  } = useContext(CreditCardContext)

  const [currentCreditCard, setCurrentCreditCard] = useState<AvailableAccount | undefined>(
    availableAccounts.at(0),
  )

  useEffect(() => {
    const payment = paymentSystems.find(
      (payment) =>
        payment.name.toLocaleLowerCase() === currentCreditCard?.paymentSystemName.toLocaleLowerCase(),
    )
    setCreditCard(payment ?? null)
    setAccountId(currentCreditCard?.accountId || '')
  }, [currentCreditCard])

  const handleChangeCreditCard = (key: Key) => {
    setCurrentCreditCard(availableAccounts.find((account) => String(account.accountId) === String(key)))
  }

  const installmentsOptions = installmentOptions.find(
    (installment) => installment.paymentSystem === currentCreditCard?.paymentSystem,
  )

  const paymentSystem = paymentSystems.find((payment) => payment.id === currentCreditCard?.paymentSystem)

  useImperativeHandle(ref, () => {
    return {
      selectedCard: {
        paymentSystem,
        validationCode,
        installmentsSelected,
        isNewCreditCard: false,
      },
    }
  }, [validationCode, installmentsSelected, paymentSystem])

  if (!availableAccounts || availableAccounts.length <= 0) return null
  return (
    <Card as="label" className="" color="secondary">
      <RadioGroup value={currentCreditCard?.accountId} onChange={handleChangeCreditCard}>
        {availableAccounts.map((creditCard) => (
          <Radio key={creditCard.accountId} value={creditCard.accountId}>
            <div className="flex  items-center justify-center">
              <CreditCard
                cardType={
                  creditCard.paymentSystemName === EFlagCreditCard.AMERICAN_EXPRESS
                    ? 'american-express'
                    : (creditCard.paymentSystemName.toLowerCase() as CardTypes)
                }
              />
              <span className="text-primary-black text-base ml-2">{creditCard.cardNumber}</span>
            </div>
          </Radio>
        ))}
      </RadioGroup>

      <div className="flex  flex-col items-start  max-w-full  gap-y-3">
        <CvvInput creditCard={paymentSystem!} onCvvChange={(cvv) => setValidationCode(cvv)} />

        <InstallmentsSelect
          containerProps={{ className: 'w-full' }}
          defaultSelectedKey={installmentsOptions?.installments.at(0)?.count}
          installments={installmentsOptions?.installments}
          // popoverWidth="100%"
          setInstallmentsSelected={setInstallmentsSelected}
        />
      </div>
    </Card>
  )
})

CreditCardList.displayName = 'CreditCardList'
export default CreditCardList
